/**
 * Hook for managing canvas image loading and auto-import functionality.
 * Uses global store for canvas images to share state across components.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStore } from '@/store';
import {
  extractCanvasImages,
  getSingleCanvasImage,
  uploadAndUpdateInput,
  type CanvasImage,
} from '@/utils/canvas';
import { InputType } from '@/types';

import type { IAppStore } from '@/store';

interface UseCanvasImagesOptions {
  /** Flow ID that triggers canvas loading when changed */
  flowId: string | undefined;
  /** Delay before loading canvas images (ms) */
  loadDelay?: number;
}

interface UseCanvasImagesReturn {
  /** Available canvas images (from global store) */
  images: CanvasImage[];
  /** Whether canvas selector modal is open */
  isOpen: boolean;
  /** Open selector for a specific input */
  openSelector: (inputId: string) => void;
  /** Close selector */
  closeSelector: () => void;
  /** Handle image selection from modal */
  handleSelect: (image: CanvasImage) => Promise<void>;
}

// Module-level tracking to prevent duplicate loading across component instances
// Tracks which flowId was loaded, not just whether loading happened
let loadedFlowId: string | null = null;

/**
 * Reset the canvas load state. Call this when navigating away from Create screen
 * or when the app state is reset to allow fresh canvas loading on next preset.
 */
export const resetCanvasLoadState = () => {
  loadedFlowId = null;
};

const selector = (store: IAppStore) => ({
  canvasImages: store.canvasImages,
  setCanvasImages: store.setCanvasImages,
  updateInput: store.updateInput,
});

export const useCanvasImages = ({
  flowId,
  loadDelay = 1000,
}: UseCanvasImagesOptions): UseCanvasImagesReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingInputId, setPendingInputId] = useState<string | null>(null);
  const hasLoaded = useRef<string | null>(null);

  // Use global store for canvas images
  const { canvasImages, setCanvasImages, updateInput } = useAppStore(selector);

  // Load canvas images and auto-import single image
  useEffect(() => {
    if (!flowId) return;
    // Skip if this flowId was already loaded (prevents React Strict Mode double-load)
    if (hasLoaded.current === flowId || loadedFlowId === flowId) return;

    hasLoaded.current = flowId;
    loadedFlowId = flowId;

    const loadImages = async () => {
      await new Promise((resolve) => setTimeout(resolve, loadDelay));

      const { inputs } = useAppStore.getState();
      const hasMediaInputs = inputs.some(
        (input) =>
          input.type === InputType.IMAGE || input.type === InputType.VIDEO
      );
      if (!hasMediaInputs) return;

      try {
        const singleImage = await getSingleCanvasImage();

        if (singleImage) {
          // Auto-import single image to first empty media input
          const latestInputs = useAppStore.getState().inputs;
          const emptyInput = latestInputs.find(
            (input) =>
              (input.type === InputType.IMAGE ||
                input.type === InputType.VIDEO) &&
              !input.src
          );

          if (emptyInput) {
            const storeUpdateInput = useAppStore.getState().updateInput;
            await uploadAndUpdateInput({
              inputId: emptyInput.id,
              image: singleImage,
              updateInput: storeUpdateInput,
            });
          }
        }

        // Load all images for manual selection into global store
        const allImages = await extractCanvasImages();
        setCanvasImages(allImages);
      } catch {
        // Silent fail - canvas images are optional
      }
    };

    loadImages();
  }, [flowId, loadDelay, setCanvasImages]);

  const openSelector = useCallback(
    async (inputId: string) => {
      // If only one image, import directly without showing modal
      if (canvasImages.length === 1) {
        await uploadAndUpdateInput({
          inputId,
          image: canvasImages[0],
          updateInput,
        });
        return;
      }

      // Multiple images - show selector modal
      setPendingInputId(inputId);
      setIsOpen(true);
    },
    [canvasImages, updateInput]
  );

  const closeSelector = useCallback(() => {
    setIsOpen(false);
    setPendingInputId(null);
  }, []);

  const handleSelect = useCallback(
    async (image: CanvasImage) => {
      if (!pendingInputId) return;

      const inputId = pendingInputId;
      closeSelector();

      await uploadAndUpdateInput({
        inputId,
        image,
        updateInput,
      });
    },
    [pendingInputId, updateInput, closeSelector]
  );

  return {
    images: canvasImages,
    isOpen,
    openSelector,
    closeSelector,
    handleSelect,
  };
};
